/**
 * SnowEffect.jsx
 * Animated snowfall effect for mobile app preview
 * Configurable intensity and speed
 */
import React, { useEffect, useState } from 'react';

const SnowEffect = ({ intensity = 'medium', speed = 'medium', enabled = true }) => {
  const [snowflakes, setSnowflakes] = useState([]);

  // Intensity settings (number of snowflakes)
  const intensityConfig = {
    light: 30,
    medium: 60,
    heavy: 100
  };

  // Speed settings (animation duration multiplier - higher = slower)
  const speedConfig = {
    slow: 2.5,      // Very slow, gentle snowfall
    medium: 1.5,    // Normal speed
    fast: 0.8       // Fast snowfall
  };

  const flakeCount = intensityConfig[intensity] || intensityConfig.medium;
  const speedMultiplier = speedConfig[speed] || speedConfig.medium;

  useEffect(() => {
    if (!enabled) {
      setSnowflakes([]);
      return;
    }

    // Generate snowflakes with varied properties
    const flakes = Array.from({ length: flakeCount }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      // Base duration 6-12 seconds, multiplied by speed setting
      animationDuration: (Math.random() * 6 + 6) * speedMultiplier,
      animationDelay: Math.random() * 4, // Reduced delay for quicker start
      size: Math.random() * 3 + 2, // 2-5px
      opacity: Math.random() * 0.3 + 0.7, // 0.7-1.0 for good visibility
    }));

    setSnowflakes(flakes);
  }, [enabled, intensity, speed, flakeCount, speedMultiplier]);

  if (!enabled) return null;

  return (
    <div 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'hidden'
      }}
    >
      <style>{`
        @keyframes snowfall-anim {
          0% {
            transform: translateY(-5px) translateX(0);
            opacity: 0;
          }
          3% {
            opacity: 1;
          }
          97% {
            opacity: 0.9;
          }
          100% {
            transform: translateY(700px) translateX(15px);
            opacity: 0;
          }
        }
        
        .snowflake-particle {
          position: absolute;
          top: -5px;
          background: white;
          border-radius: 50%;
          animation: snowfall-anim linear infinite;
          box-shadow: 0 0 2px rgba(255,255,255,0.8), 0 0 4px rgba(200,220,255,0.5);
        }
      `}</style>
      
      {snowflakes.map(flake => (
        <div
          key={flake.id}
          className="snowflake-particle"
          style={{
            left: `${flake.left}%`,
            width: `${flake.size}px`,
            height: `${flake.size}px`,
            opacity: flake.opacity,
            animationDuration: `${flake.animationDuration}s`,
            animationDelay: `${flake.animationDelay}s`
          }}
        />
      ))}
    </div>
  );
};

export default SnowEffect;
